import {
	CategoryScale,
	Chart as ChartJS,
	Colors,
	Legend,
	LinearScale,
	LineElement,
	Plugin,
	PointElement,
	Title,
	Tooltip,
} from 'chart.js';
import { AnyObject } from 'chart.js/dist/types/basic';
import { uniqueId } from 'lodash';
import React from 'react';
import { Line } from 'react-chartjs-2';

import { afterUpdateFn, colors, GraphData } from './common';
import {
	container as containerStyle,
	legendContainer as legendContainerStyle,
	lineGraph as lineGraphStyle,
	subtitleContainer as subtitleContainerStyle,
	titleContainer as titleContainerStyle,
} from './styles.module.css';

ChartJS.register(
	CategoryScale,
	Colors,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
);

interface LineGraphProps {
	displayTitle: string;
	subtitle: string;
	dataItems: GraphData[];
}

// Picks up legend for other
const htmlLegendPlugin = (legends?: string[]): Plugin<'line', AnyObject> => ({
	id: 'htmlLegend',
	afterUpdate: afterUpdateFn({ legends, includeXValues: false }),
});

export const LineGraph = ({ displayTitle, subtitle, dataItems }: LineGraphProps) => {
	const legends = dataItems.map((set) => set.label ?? '');
	// First get a unique flat map of 'labels' (x-values), then if they are all dates,
	// sort them by date, then map the data items to the sorted labels. If there is any
	// data missing (eg. where one data set doesn't cover the range of the other), simply don't plot it
	const rawXLabels = dataItems.flatMap((set) => set.xValues ?? []);
	const unsortedXLabels = [...new Set(rawXLabels)];
	const sortedXLabels = dataItems.every((set) => set.kind === 'date')
		? unsortedXLabels.slice().sort((a, b) => {
				const dateA = new Date(a);
				const dateB = new Date(b);
				return dateA.getTime() - dateB.getTime();
		  })
		: unsortedXLabels;

	const sortedDatasets = dataItems.map((set, index) => {
		return {
			label: set.label,
			data: sortedXLabels.map((label) => {
				const index = unsortedXLabels.indexOf(label);
				// Return undefined not zero if there is no data at this index
				return set.yValues?.[index];
			}),
			backgroundColor: colors[index % colors.length],
			borderColor: colors[index % colors.length],
		};
	});
	const legendContainerId = uniqueId('legend-container-');

	// @todo: CSS
	const options = {
		responsive: true,
		// We are taking over the AR (setting height/width in CSS)
		maintainAspectRatio: false,
		scales: {
			x: {
				grid: {
					display: false,
				},
				ticks: {
					color: '#1a1919b2',
					font: {
						family: 'Avenir',
						size: 15,
						weight: '400',
					},
					padding: 27,
				},
			},
			y: {
				title: {
					display: true,
					text: sortedDatasets[0].label ?? '',
					color: '#1a1919b2',
					font: {
						family: 'Avenir',
						size: 12,
						weight: '400',
						lineHeight: 1.4,
					},
					padding: { top: 0, left: 0, right: 0, bottom: 12 },
				},
				// @todo: if y-labels are '%', suppress title above but format tick #s with '%' suffix
				ticks: {
					color: '#1a1919b2',
					font: {
						family: 'Avenir',
						size: 15,
						weight: '400',
						lineHeight: 1.4,
					},
					maxTicksLimit: 5,
				},
			},
		},
		plugins: {
			htmlLegend: {
				containerID: legendContainerId,
			},
			legend: {
				display: false,
			},
			title: {
				display: false,
			},
		},
	};

	const data = {
		labels: sortedXLabels,
		datasets: sortedDatasets,
	};

	return (
		<div className={containerStyle}>
			<div className={titleContainerStyle}>{displayTitle}</div>
			<div id={legendContainerId} className={legendContainerStyle}>
				{/* @todo: replace IDs with refs */}
				<ul></ul>
			</div>
			<div className={lineGraphStyle}>
				<Line data={data} options={options} plugins={[htmlLegendPlugin(legends)]} />
			</div>
			<div className={subtitleContainerStyle}>{subtitle}</div>
		</div>
	);
};
